import { useT } from "../lib/i18n.jsx";

const btn =
  "flex h-10 w-10 items-center justify-center bg-white text-slate-700 " +
  "shadow-md active:bg-slate-100 text-lg font-semibold select-none";

/** Zoom, compass (reset north), reset view. `map` is the live MapLibre map.
 *
 *  `compact` is a phone (item 0093): the controls stack in one column and
 *  "reset view" becomes an icon rather than a labelled bar across the
 *  bottom-left. `docked` (item 0373): the menu is at the top, so on a phone
 *  that column sits bottom-right. Above phone width the bottom corners were
 *  already free of the menu, so a portrait totem keeps this layout as is. */
export default function MapControls({
  map,
  homeCamera,
  compact = false,
  stacked = false,
  docked = false,
  children,
}) {
  const { t } = useT();
  if (!map) return null;

  const resetNorth = () => map.easeTo({ bearing: 0, duration: 500 });
  const resetView = () => homeCamera && map.easeTo({ ...homeCamera, duration: 900 });

  if (compact) {
    return (
      <div
        className={
          "absolute z-10 flex flex-col gap-2 " +
          // Portrait phone (item 0373): the menu owns the TOP, so the column
          // moves to the bottom-right corner, 40px up to leave MapLibre's
          // attribution button its corner. ViewerPage stops the menu's column
          // above it (dockReserve), so the two never meet. Landscape phone
          // keeps the left column under its logo button.
          (docked ? "bottom-10 right-3 items-end" : "left-3 top-20 items-start")
        }
        data-map-controls
      >
        <div className="flex flex-col overflow-hidden rounded-lg">
          <button className={btn} onClick={() => map.zoomIn()} aria-label="Zoom in">
            +
          </button>
          <div className="h-px bg-slate-200" />
          <button className={btn} onClick={() => map.zoomOut()} aria-label="Zoom out">
            −
          </button>
        </div>
        <button
          className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800
                     text-white shadow-md"
          onClick={resetNorth}
          aria-label="Reset north"
        >
          <svg viewBox="0 0 24 24" width="22" height="22">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M12 4l3 8-3-1.6L9 12z" fill="#e63946" />
            <path d="M12 20l-3-8 3 1.6 3-1.6z" fill="#cbd5e1" />
          </svg>
        </button>
        <button
          className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800
                     text-lg text-white shadow-md"
          onClick={resetView}
          aria-label={t("reset_view")}
          title={t("reset_view")}
        >
          ⟲
        </button>
        {/* Phone-only extras, e.g. the QR button (item 0324). */}
        {children}
      </div>
    );
  }

  return (
    <>
      <div className="absolute bottom-24 right-3 z-10 flex flex-col overflow-hidden rounded-lg">
        <button className={btn} onClick={() => map.zoomIn()} aria-label="Zoom in">
          +
        </button>
        <div className="h-px bg-slate-200" />
        <button className={btn} onClick={() => map.zoomOut()} aria-label="Zoom out">
          −
        </button>
      </div>
      {/* bottom-24 (96px) + two 40px zoom buttons ≈ 177px top edge — the
          compass needs to clear that, so 192px. */}
      <button
        className="absolute bottom-48 right-3 z-10 flex h-10 w-10 items-center justify-center
                   rounded-full bg-slate-800 text-white shadow-md"
        onClick={resetNorth}
        aria-label="Reset north"
      >
        <svg viewBox="0 0 24 24" width="22" height="22">
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M12 4l3 8-3-1.6L9 12z" fill="#e63946" />
          <path d="M12 20l-3-8 3 1.6 3-1.6z" fill="#cbd5e1" />
        </svg>
      </button>
      <button
        className={
          // bottom-3 in the stacked band (item 0314): one row with the language
          // picker, clear of the directions panel above.
          "absolute left-3 z-10 rounded-lg bg-slate-800 px-4 py-2.5 text-sm font-medium " +
          "text-white shadow-md active:bg-slate-700 " +
          (stacked ? "bottom-3" : "bottom-6")
        }
        onClick={resetView}
      >
        ⟲ {t("reset_view")}
      </button>
    </>
  );
}
